import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Sparkles } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TemplatePicker = ({ isOpen, onClose, onSelect, currentTemplate }) => {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(currentTemplate || "freestyle");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    const fetchTemplates = async () => {
      try {
        const resp = await axios.get(`${API}/templates`);
        setTemplates(resp.data.templates);
      } catch {}
      setLoading(false);
    };
    fetchTemplates();
  }, [isOpen]);

  useEffect(() => {
    setSelected(currentTemplate || "freestyle");
  }, [currentTemplate]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.92, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.92, opacity: 0, y: 20 }}
          className="bg-[#F8F6F1] rounded-3xl max-w-3xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-[#D4C9B8]/40"
          onClick={(e) => e.stopPropagation()}
          data-testid="template-picker-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-[#D4C9B8]/30">
            <div>
              <h2 className="font-serif text-2xl font-medium text-[#2D3A2D]">Choose a Template</h2>
              <p className="font-sans text-sm text-[#7A8A7A] mt-1">Pick a theme to guide your story</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-[#E5D4C1]/50 rounded-xl transition-colors" data-testid="close-template-picker">
              <X className="w-5 h-5 text-[#7A8A7A]" />
            </button>
          </div>

          {/* Grid */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-3 border-[#7A9B7E] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {templates.map((t) => {
                  const isSelected = selected === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setSelected(t.id)}
                      className={`relative text-left rounded-2xl overflow-hidden border-2 transition-all duration-200 hover:shadow-lg ${
                        isSelected
                          ? "border-[#7A9B7E] shadow-lg scale-[1.02]"
                          : "border-transparent hover:border-[#D4C9B8]"
                      }`}
                      data-testid={`template-option-${t.id}`}
                    >
                      {/* Cover image */}
                      <div className="h-28 overflow-hidden" style={{
                        background: t.cover_image ? undefined : `linear-gradient(135deg, ${t.theme?.gradient_from || '#9FB89F'}, ${t.theme?.gradient_to || '#5A7B5E'})`
                      }}>
                        {t.cover_image ? (
                          <img src={t.cover_image} alt={t.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Sparkles className="w-8 h-8 text-white/50" />
                          </div>
                        )}
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-[#7A9B7E] rounded-full flex items-center justify-center shadow-md">
                            <Check className="w-3.5 h-3.5 text-white" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="p-3 bg-white">
                        <h3 className="font-sans text-sm font-semibold text-[#2D3A2D] leading-tight">{t.name}</h3>
                        <p className="font-sans text-xs text-[#7A8A7A] mt-0.5 line-clamp-2 leading-relaxed">{t.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-[#D4C9B8]/30 flex items-center justify-between">
            <p className="font-sans text-xs text-[#7A8A7A]">
              You can switch templates anytime
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-5 py-2.5 rounded-full font-sans text-sm text-[#7A8A7A] hover:bg-[#E5D4C1]/50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { onSelect(selected); onClose(); }}
                className="px-6 py-2.5 rounded-full font-sans text-sm font-medium bg-[#4A5D4A] text-white hover:bg-[#2D3A2D] transition-colors shadow-md"
                data-testid="confirm-template-btn"
              >
                Apply Template
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TemplatePicker;
